import React from "react";
import { Button } from "react-bootstrap";
import moment from "moment";

/**
 * Right-column panel shown when a DRAFT row is selected in the requestimates list.
 * A draft isn't a live job, so instead of the normal detail/action panel we offer
 * exactly two choices: resume it, or delete it.
 *
 * A draft has no joined vehicle/customer/service rows yet — the entered detail
 * lives in the snapshot (draft_payload). The parent loads that via getDraft and
 * passes it as `payload`; we prefer it and fall back to whatever denormalized
 * fields the list row carries.
 *
 * Props:
 *   draft          the selected list item (draft row)
 *   payload        the draft_payload snapshot (may be null while loading)
 *   currentUserId  the viewing user's User_id (to label a colleague's draft)
 *   onContinue     (draft) => void   resume editing in the wizard
 *   onDelete       (draft) => void   hard-delete the draft (parent confirms)
 *   deleting       boolean           spins the Delete action
 */
const STEP_LABELS = ["", "Specialist / Customer", "Vehicle", "Work", "Notes", "Dates"];

const DraftActionPanel = ({
  draft,
  payload,
  currentUserId,
  onContinue,
  onDelete,
  deleting = false,
}) => {
  if (!draft) return null;

  const v = (payload && payload.vehicle) || null;
  const vehicleDesc = v
    ? [v.color, v.year, v.make, v.model].filter(Boolean).join(" ")
    : [draft.vehicleColor, draft.vehicleYear, draft.vehicleMake, draft.vehicleModel]
        .filter(Boolean)
        .join(" ");
  const vin = (v && v.vin) || draft.vin || null;
  const title = vehicleDesc || vin || "Vehicle not entered";
  const showVinLine = vin && vehicleDesc; // only when title isn't already the VIN

  const service = (payload && payload.serviceRequested) || draft.serviceRequested;
  const party =
    (payload && (payload.customerName || draft.customerName)) ||
    draft.customerName ||
    draft.dealerName ||
    draft.installerName ||
    null;
  const partyType = payload && payload.customerType;

  const started =
    draft.created_date && moment(draft.created_date).isValid()
      ? moment(draft.created_date).format("MMM D, YYYY")
      : null;
  const ownedByOther =
    draft.draft_owner_id && String(draft.draft_owner_id) !== String(currentUserId);
  const stepLabel = STEP_LABELS[draft.draft_step] || null;

  return (
    <div className="bg-white border border-secondary p-4">
      <div className="d-flex align-items-center mb-3">
        <i className="fa-sharp fa-solid fa-file-pen text-secondary mr-2" style={{ fontSize: "20px" }} />
        <h5 className="mb-0">Draft request</h5>
      </div>

      <div className="mb-3">
        <div className="font-weight-bold">{title}</div>
        {showVinLine && (
          <div className="text-muted" style={{ fontSize: "13px" }}>
            VIN {vin}
          </div>
        )}
        <div className="text-muted" style={{ fontSize: "14px" }}>
          {service || "No work selected yet"}
        </div>
      </div>

      {party && (
        <div className="mb-3">
          <i className="fa-sharp fa-regular fa-user text-muted mr-2" />
          {party}
          {partyType && (
            <span className="badge badge-light ml-2 text-uppercase" style={{ fontSize: "0.65rem" }}>
              {partyType === "whole" ? "Wholesale" : partyType}
            </span>
          )}
        </div>
      )}

      <ul className="list-unstyled text-muted mb-4" style={{ fontSize: "13px" }}>
        {stepLabel && (
          <li>
            <i className="fa-sharp fa-regular fa-list-check mr-2" />
            Left off at the <strong>{stepLabel}</strong> step
          </li>
        )}
        {started && (
          <li>
            <i className="fa-sharp fa-regular fa-clock mr-2" />
            Started {started}
          </li>
        )}
        {ownedByOther && draft.draft_owner_name && (
          <li>
            <i className="fa-sharp fa-regular fa-user mr-2" />
            Started by {draft.draft_owner_name}
          </li>
        )}
      </ul>

      <div className="d-flex">
        <Button
          variant="primary"
          className="mr-2"
          onClick={() => onContinue(draft)}
          disabled={deleting}
        >
          <i className="fa-sharp fa-regular fa-pen-to-square mr-2" />
          Continue editing
        </Button>
        <Button
          variant="outline-danger"
          onClick={() => onDelete(draft)}
          disabled={deleting}
        >
          {deleting ? (
            <span><i className="fa-sharp fa-regular fa-spinner fa-spin" /> Deleting…</span>
          ) : (
            <span><i className="fa-sharp fa-regular fa-trash mr-2" />Delete</span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default DraftActionPanel;
